import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setExpandedKeys } from "../../../../redux/slices/contractsSlice";
// import { format } from "date-fns";
import { laneApi } from "../api/laneApi";
import { routeApi } from "../api/routeApi";
import { rateApi } from "../api/rateApi";
import { contractApi } from "../api/contractApi";
import { bulkUploadApi } from "../api/bulkUploadApi";
import { accessorialApi } from '../api/accessorialApi';
// import Cookies from "js-cookie";
// import { convertDateToUTC } from "../utils/formatters";
import { RateEditor } from '../components/RateEditor';
import { FacilityTypeahead } from '../components/FacilityTypeahead';
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import { ToggleSwitch } from "../components/ToggleSwitch";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { 
  Download, 
  Edit, 
  FileText, 
  X, 
  MapPin, 
  Route, 
  DollarSign,
  Plus,
  Save
} from "lucide-react";
import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { formatAsLocalDate } from "../utils/formatters";
import Cookies from "js-cookie";

export const ContractDetailsContent = ({ contractData, officeCode, expandedDataKey=null, tabId=null }) => {
  const dispatch = useDispatch();
  const [accessorialMap, setAccessorialMap] = useState({});
  // Controls whether to show only active rates (endDate >= today) or all rates
  const [activeOnly, setActiveOnly] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  // Seeded from Redux (keyed by this tab's id) rather than always starting empty, so
  // switching away from this tab — or leaving the Contracts module entirely — and
  // coming back restores whatever the user had expanded instead of collapsing it.
  const persistedExpandedKeys = useSelector((state) =>
    tabId ? state.contracts.expandedKeysByTab?.[tabId] : undefined
  );
  const [hierarchicalExpandedItems, setHierarchicalExpandedItems] = useState(() => {
    if (persistedExpandedKeys?.length) return new Set(persistedExpandedKeys);
    return expandedDataKey ? new Set([expandedDataKey]) : new Set();
  });
  // Mirror to Redux as an effect (not inline in the setState updater above) — a
  // reducer dispatched from inside a state-updater function can fire while React
  // is still rendering a different component, which is exactly what React's
  // "Cannot update a component while rendering a different component" guards
  // against.
  useEffect(() => {
    if (tabId) dispatch(setExpandedKeys({ tabId, keys: Array.from(hierarchicalExpandedItems) }));
  }, [tabId, dispatch, hierarchicalExpandedItems]);
  // const [localContractData, setLocalContractData] = useState(contractData);
  // Contract-level fields (Effective Date, Status, etc.) can change outside this tab
  // (e.g. bulk Excel update). The tab holds a snapshot in Redux, so refetch the
  // canonical record whenever the tab mounts or another flow reports an update.
  const [liveContract, setLiveContract] = useState(null);
  const lastContractsUpdatedAt = useSelector(
    (state) => state.contracts.lastContractsUpdatedAt
  );
  const displayContractData = liveContract || contractData;
  const token = Cookies.get("authToken");

  const fallbackOfficeCode = "DIAMOND";
  const headerRef = useRef(null);
  const [associatedData, setAssociatedData] = useState({
    lanes: { data: [], error: null },
    routes: { data: [], error: null },
    rates: { data: [], error: null },
    loading: true,
  });
  const hierarchicalData = useMemo(() => {
    if (!associatedData.lanes?.data || !associatedData.routes?.data || !associatedData.rates?.data) {
      return [];
    }

// Get today's date in UTC format (YYYY-MM-DD) for consistent comparison with backend dates
const todayUTC = new Date().toISOString().slice(0, 10); // "2026-03-13" in UTC
console.log("todayUTC:", todayUTC);



// When activeOnly is true, filter rates where today falls between startDate and endDate
// Rate is active only when: startDate <= today <= endDate
// When activeOnly is false, show all rates regardless of dates
// Backend dates come as UTC datetime (e.g. "2026-03-13T00:00:00"), slice to get date only
const filteredRates = activeOnly
  ? associatedData.rates.data.filter(rate => {
      // Convert backend UTC dates to UTC date string
      const startDate = new Date(rate.startDate).toISOString().slice(0, 10);
      const endDate = new Date(rate.endDate).toISOString().slice(0, 10);
      const started = !startDate || startDate <= todayUTC;
      const notExpired = !endDate || endDate >= todayUTC;

      console.log(`Rate: ${rate.rateCode} | startDate: ${startDate} | endDate: ${endDate} | started: ${started} | notExpired: ${notExpired} | show: ${started && notExpired}`);


      // Rate is active only when: startDate <= today <= endDatesss
      return started && notExpired;
    })
  : associatedData.rates.data;

  console.log("filteredRates count:", filteredRates.length);
  console.log("total rates count:", associatedData.rates.data.length);
    

    const buildHierarchicalData = (lanes, routes, rates) => {
      return lanes.map(lane => ({
        ...lane,
        type: 'lane',
        children: routes
          .filter(route => route.laneId === lane.id)
          .map(route => ({
            ...route,
            type: 'route',
            children: rates
              .filter(rate => rate.routeId === route.id)
              .map(rate => ({
                ...rate,
                type: 'rate'
              }))
          }))
      }));
    };
    
    return buildHierarchicalData(
      associatedData.lanes.data,
      associatedData.routes.data,
      filteredRates
    );
  }, [associatedData.lanes.data, associatedData.routes.data, associatedData.rates.data, activeOnly]);

  // useEffect(() => {
  //   setLocalContractData(contractData);
  // }, [contractData]);

  // 
  // Build parent-child relationship map
  const parentMap = useMemo(() => {
    const map = {};
    
    hierarchicalData.forEach(lane => {
      const laneKey = `lane-${lane.id}`;
      
      lane.children?.forEach(route => {
        const routeKey = `route-${route.id}`;
        map[routeKey] = laneKey;
        
        route.children?.forEach(rate => {
          const rateKey = `rate-${rate.id}`;
          map[rateKey] = routeKey;
        });
      });
    });
    
    return map;
  }, [hierarchicalData]);

  // Get all ancestor keys for a given key
  const getAncestorKeys = useCallback((key) => {
    const ancestors = [];
    let currentKey = key;
    
    while (parentMap[currentKey]) {
      const parentKey = parentMap[currentKey];
      ancestors.push(parentKey);
      currentKey = parentKey;
    }
    
    return ancestors;
  }, [parentMap]);

  // Get all descendant keys for a given key
  const getDescendantKeys = useCallback((key) => {
    const [type, id] = key.split('-');
    const descendants = [];
    const numId = parseInt(id);
    
    if (type === 'lane') {
      const lane = hierarchicalData.find(l => l.id === numId);
      if (lane?.children) {
        lane.children.forEach(route => {
          descendants.push(`route-${route.id}`);
          if (route.children) {
            route.children.forEach(rate => {
              descendants.push(`rate-${rate.id}`);
            });
          }
        });
      }
    } else if (type === 'route') {
      for (const lane of hierarchicalData) {
        const route = lane.children?.find(r => r.id === numId);
        if (route?.children) {
          route.children.forEach(rate => {
            descendants.push(`rate-${rate.id}`);
          });
          break;
        }
      }
    }
    
    return descendants;
  }, [hierarchicalData]);

  // Enhanced toggle function with parent/child expansion logic
  const toggleHierarchicalExpand = useCallback((id, type) => {
    const key = `${type}-${id}`;
    
    setHierarchicalExpandedItems(prev => {
      const newExpanded = new Set(prev);
      
      if (newExpanded.has(key)) {
        // COLLAPSING: Remove this item and all its descendants
        newExpanded.delete(key);
        const descendants = getDescendantKeys(key);
        descendants.forEach(descKey => newExpanded.delete(descKey));
      } else {
        // EXPANDING: Add this item and all its ancestors
        newExpanded.add(key);
        const ancestors = getAncestorKeys(key);
        ancestors.forEach(ancestorKey => newExpanded.add(ancestorKey));
      }
      
      return newExpanded;
    });
  }, [getAncestorKeys, getDescendantKeys]);

  // Handle initial expandedDataKey with ancestor expansion
  useEffect(() => {
    if (expandedDataKey && hierarchicalData.length > 0) {
      setHierarchicalExpandedItems((prev) => {
        const newExpanded = new Set(prev);
        
        // Add the target key
        newExpanded.add(expandedDataKey);
        
        // Add all ancestor keys to ensure the path is visible
        const ancestors = getAncestorKeys(expandedDataKey);
        ancestors.forEach(ancestorKey => newExpanded.add(ancestorKey));
        
        return newExpanded;
      });
    }
  }, [expandedDataKey, hierarchicalData.length, getAncestorKeys]);



  useEffect(() => {
    const offCode = contractData?.officeCode;
    if (!contractData?.id || !offCode) return;

    let cancelled = false;
    contractApi
      .getContractById(offCode, contractData.id)
      .then((response) => {
        if (!cancelled && response?.data) {
          setLiveContract(response.data);
        }
      })
      .catch(() => {
        // Keep showing the last known-good data (prop snapshot or previous fetch)
      });

    return () => {
      cancelled = true;
    };
  }, [contractData?.id, contractData?.officeCode, lastContractsUpdatedAt]);

  useEffect(() => {
    const fetchAssociatedData = async () => {
      const offCode = contractData.officeCode;

      if (!contractData?.id || !offCode ) return;

      const pageable = { page: 0, size: 15, sort: "id,asc" };

      const results = await Promise.allSettled([
        laneApi.filterSingleOfficeCodeLanes(offCode, { contractId: contractData.id }, pageable),
        routeApi.filterSingleOfficeCodeRoutes(offCode, { contractId: contractData.id }, pageable),
        rateApi.filterSingleOfficeCodeRates(offCode, { contractId: contractData.id }, { page: 0, size: 1000, sort: "id,asc" }),
      ]);

      const lanes = results[0].status === "fulfilled" ? results[0].value.data.content : [];
      const routes = results[1].status === "fulfilled" ? results[1].value.data.content : [];
      const rates = results[2].status === "fulfilled" ? results[2].value.data.content : [];

      setAssociatedData({
        lanes: { data: lanes, error: null },
        routes: { data: routes, error: null },
        rates: { data: rates, error: null },
        loading: false,
      });
    };

    fetchAssociatedData();
  }, [contractData, officeCode]);

  useEffect(() => {
    const getAccessorialMap = async () => {
      const map = await fetchAccessorials(officeCode);
      setAccessorialMap(map || {});
    };
    getAccessorialMap();
  }, [officeCode]);


  // const toggleHierarchicalExpand = useCallback((id, type) => {
  //   const key = `${type}-${id}`;
  //   const newExpanded = new Set(hierarchicalExpandedItems);
  //   if (newExpanded.has(key)) {
  //     newExpanded.delete(key);
  //   } else {
  //     newExpanded.add(key);
  //   }
  //   //   //   setHierarchicalExpandedItems(newExpanded);
  // }, [hierarchicalExpandedItems]);

  useEffect(() => {
    if (expandedDataKey) {
      setHierarchicalExpandedItems((prev) => {
        const newExpanded = new Set(prev);
        newExpanded.add(expandedDataKey);
        return newExpanded;
      });
    }
  }, [expandedDataKey]);

  const expandAllHierarchical = () => {
    const allKeys = new Set();
    hierarchicalData.forEach(lane => {
      allKeys.add(`lane-${lane.id}`);
      lane.children?.forEach(route => {
        allKeys.add(`route-${route.id}`);
        route.children?.forEach(rate => {
          allKeys.add(`rate-${rate.id}`);
        });
      });
    });
    setHierarchicalExpandedItems(allKeys);
  };

  const collapseAllHierarchical = () => {
    setHierarchicalExpandedItems(new Set());
  };

  const isHierarchicalExpanded = (id, type) => {
    return hierarchicalExpandedItems.has(`${type}-${id}`);
  };

  const headerInfo = displayContractData
  ? {
      "Contract Code": {
        type: "text",
        value: displayContractData.contractCode || "",
        readonly: true,
      },
      Status: {
        type: "text",
        value: displayContractData.status || "ACTIVE",
        options: ["ACTIVE", "DRAFT", "EXPIRED"],
        readonly: !isEditMode,
      },
      "Payment Method": {
        type: "text",
        value: displayContractData.paymentMethod || "—",
        readonly: true,
      },
      "Effective Date": {
        type: "text",
        value: displayContractData.effectiveDate
                    ? formatAsLocalDate(displayContractData.effectiveDate)
                    : "—",
        readonly: !isEditMode,
      },
      "Expiration Date": {
        type: "text",
        value: displayContractData.expirationDate
                    ? formatAsLocalDate(displayContractData.expirationDate)
                    : "—",
        readonly: !isEditMode,
      },
      "Lanes Count": {
        type: "text",
        value: displayContractData.laneIds?.length ?? 0,
        readonly: true,
      },
    }
  : {};


  // const headerInfo = localContractData
  //   ? {
  //       "Contract Code": {
  //         type: "text",
  //         value: localContractData.contractCode || "",
  //         readonly: true,
  //       },
  //       Status: {
  //         type: isEditMode ? "dropdown" : "text",
  //         value: localContractData.status || "ACTIVE",
  //         options: ["ACTIVE", "DRAFT", "EXPIRED"],
  //         readonly: !isEditMode,
  //       },
  //       "Effective Date": {
  //         type: isEditMode ? "date" : "text",
  //         value: isEditMode ? 
  //           localContractData.effectiveDate
  //           : localContractData["Effective Date"] ||  "",
  //         readonly: !isEditMode,
  //       },
  //       "Expiration Date": {
  //         type: isEditMode ? "date" : "text",
  //         value: isEditMode ? 
  //           localContractData.expirationDate
  //           : localContractData["Expiration Date"] ||  "",
  //         readonly: !isEditMode,
  //       },
  //       "Lanes Count": {
  //         type: "text",
  //         value: localContractData.laneIds?.length ?? 0,
  //         readonly: true,
  //       },
  //     }
  //   : {};


  // const handleHeaderChange = async (fieldKey, newValue) => {
  //   if (!isEditMode) return;

  //   const keyMap = {
  //     "Contract Code": "contractCode",
  //     Status: "status",
  //     "Effective Date": "effectiveDate",
  //     "Expiration Date": "expirationDate",
  //     "Lanes Count": "laneIds",
  //   };

  //   const actualKey = keyMap[fieldKey];

  //   setLocalContractData((prev) => ({
  //     ...prev,
  //     [actualKey]: newValue,
  //   }));
  //     //   
  //   try {
  //     const token = Cookies.get("authToken");
  //     const officeCodesCookie = Cookies.get("officeCodes");
  //     const officeCodes = officeCodesCookie ? officeCodesCookie.split(",") : [];

  //     
  //     // Find parent office code using regex
  //     const findParentOffice = (codes) => {
  //       return codes.find((code) => {
  //         const pattern = new RegExp(`^${code}-`);
  //         return codes.some((other) => other !== code && pattern.test(other));
  //       });
  //     };

  //     const parentOfficeCode = findParentOffice(officeCodes);
  //     const officeCode = parentOfficeCode || officeCodes[0];

  //     
  //     if (!officeCode || !contractData?.id) {
  //       console.error("Missing officeCode or contractId for update.");
  //       return;
  //     }

  //     // Convert date fields to UTC before sending
  //     const updatedContractDTO = {
  //       ...localContractData,
  //       [actualKey]: newValue,
  //       effectiveDate: convertDateToUTC(contractData.effectiveDate),
  //       expirationDate: convertDateToUTC(contractData.expirationDate),
  //     };

  //     const response = await contractApi.updateContract(
  //       officeCode,
  //       contractData.id,
  //       updatedContractDTO
  //     );

  //       //   } catch (error) {
  //     console.error("Failed to update contract:", error);
  //   }
  // };




  const handleExportData = async () => {
    try {
            const fileBlob = await bulkUploadApi.downloadContracts(officeCode);

      const url = window.URL.createObjectURL(new Blob([fileBlob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Contracts.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Failed to export contract data:', error);
    }
  };

  const headerButtons = [
    {
      label: "Export",
      icon: <Download size={16} />,
      onClick: handleExportData,
      variant: "primary"
    },
    {
      label: isEditMode ? "Exit Edit" : "Edit Mode",
      icon: isEditMode ? <X size={16} /> : <Edit size={16} />,
      onClick: () => setIsEditMode(!isEditMode),
      variant: isEditMode ? "secondary" : "primary"
    }
  ];

  const fetchAccessorials = async (officeCode) => {
    const mapAccessorials = (accessorials) =>
      accessorials.reduce((map, { accessorialCode, accessorialName, conditionNotes }) => {
        map[accessorialCode] = { accessorialName, conditionNotes };
        return map;
      }, {});

    const tryFetchAndMap = async (code, isFallback = false) => {
      try {
        const response = await accessorialApi.getAccessorials(code);
        if (Array.isArray(response.data) && response.data.length > 0) {
          return mapAccessorials(response.data);
        }
        if (!isFallback) {
          console.warn(`Accessorials list empty for ${code}. Falling back to ${fallbackOfficeCode}...`);
        }
      } catch (err) {
        console.error(`Error fetching ${isFallback ? 'fallback ' : ''}accessorials for ${code}:`, err);
        if (!isFallback) {
          console.warn(`Falling back to ${fallbackOfficeCode}...`);
        }
      }
      return null;
    };

    const primaryMap = await tryFetchAndMap(officeCode);
    if (primaryMap) return primaryMap;
    return await tryFetchAndMap(fallbackOfficeCode, true);
  };

  // Fetches only rates from backend for this contract
// Used separately from fetchAssociatedData to avoid re-fetching lanes and routes
// Called on toggle change and after any rate create/update/delete

  const fetchRates = useCallback(async () => {
  const offCode = contractData.officeCode;
  if (!contractData?.id || !offCode) return;

  try {
    const result = await rateApi.filterSingleOfficeCodeRates(
      offCode,
      { contractId: contractData.id },
      { page: 0, size: 1000, sort: "id,asc" }
    );
    setAssociatedData(prev => ({
      ...prev,
      rates: { data: result.data.content, error: null },
    }));
    return result.data.content;
  } catch (err) {
    console.error("Failed to fetch rates:", err);
    return null;
  }
}, [contractData]);

// When toggle changes:
// 1. Collapse the tree immediately — gives user clear visual feedback
// 2. Re-fetch rates from backend — ensures fresh data, not stale
// Skip if initial data is still loading

useEffect(() => {
  if (!associatedData.loading) {
    collapseAllHierarchical();     
    fetchRates();
  }
}, [activeOnly]);

// Re-fetch rates from backend after a rate is created or updated, so the UI
// reflects the real backend record (real id, not a temp copy id) rather than
// a locally-guessed one.
const handleRateUpdate = useCallback(async (rateIndex, updatedRate) => {
  const freshRates = await fetchRates();
  // A newly-created rate's id changes from its temp "_copy_" id to a real
  // backend id on this refetch — carry the expanded state over by matching on
  // rateCode (stable across that transition) so it doesn't appear to collapse
  // the moment it's saved.
  if (updatedRate?.rateCode && freshRates) {
    const match = freshRates.find((r) => r.rateCode === updatedRate.rateCode);
    if (match) {
      setHierarchicalExpandedItems((prev) => {
        const next = new Set(prev);
        next.add(`rate-${match.id}`);
        return next;
      });
    }
  }
}, [fetchRates, setHierarchicalExpandedItems]);

// Inserts a not-yet-saved rate (e.g. a copy the user is about to fill in and
// create) directly into local state — there's nothing to fetch yet, since it
// doesn't exist on the backend until the user saves it. Expanded by default so
// the user can immediately see and edit it, matching however the rest of the
// tree is expanded.
const handleRateAdd = useCallback((rateIndex, newRate) => {
  setAssociatedData(prev => {
    const newRates = [...prev.rates.data];
    newRates.splice(rateIndex + 1, 0, newRate);
    return {
      ...prev,
      rates: { ...prev.rates, data: newRates }
    };
  });
  setHierarchicalExpandedItems((prev) => {
    const next = new Set(prev);
    next.add(`rate-${newRate.id}`);
    return next;
  });
}, [setHierarchicalExpandedItems]);

// Discards an unsaved rate draft locally (Cancel on a not-yet-created rate) —
// again, nothing to fetch since it was never persisted.
const handleRateRemove = useCallback((rateIndex, removedRate) => {
  setAssociatedData(prev => {
    const newRates = [...prev.rates.data];
    newRates.splice(rateIndex, 1);
    return {
      ...prev,
      rates: { ...prev.rates, data: newRates }
    };
  });
  if (removedRate?.id) {
    setHierarchicalExpandedItems((prev) => {
      if (!prev.has(`rate-${removedRate.id}`)) return prev;
      const next = new Set(prev);
      next.delete(`rate-${removedRate.id}`);
      return next;
    });
  }
}, [setHierarchicalExpandedItems]);

  const HierarchicalLaneItem = ({ lane }) => {
    const hasRoutes = lane.children && lane.children.length > 0;
    const expanded = isHierarchicalExpanded(lane.id, 'lane');

    const [laneEditData, setLaneEditData] = useState(lane);
    const [laneOriginalData, setLaneOriginalData] = useState(lane);
    const [laneHasChanges, setLaneHasChanges] = useState(false);
    const [laneSaving, setLaneSaving] = useState(false);
    const [laneError, setLaneError] = useState(null);

    // Reset local editor state when lane changes (e.g. refetch after save)
    useEffect(() => {
      setLaneEditData(lane);
      setLaneOriginalData(lane);
      setLaneHasChanges(false);
      setLaneError(null);
    }, [lane]);

    // If user exits global edit mode, drop local unsaved changes
    useEffect(() => {
      if (!isEditMode) {
        setLaneEditData(lane);
        setLaneOriginalData(lane);
        setLaneHasChanges(false);
        setLaneError(null);
      }
    }, [isEditMode, lane]);

    const handleLaneFieldChange = (field, value) => {
      const updated = {
        ...laneEditData,
        [field]: value,
      };
      setLaneEditData(updated);

      // Fields we actually care about for "has changes"
      const trackedFields = [
        'name',
        'originCountry',
        'originState',
        'originCity',
        'origin',             // zip / code
        'destinationCountry',
        'destinationState',
        'destinationCity',
        'destination',        // zip / code
        'viaPoint',
        'viaPointAddress1',
        'viaPointAddress2',
        'viaPointCity',
        'viaPointState',
        'viaPointCountry',
        'viaPointZip',
        'viaPoint2',
        'viaPoint2Address1',
        'viaPoint2Address2',
        'viaPoint2City',
        'viaPoint2State',
        'viaPoint2Country',
        'viaPoint2Zip',
      ];

      const hasDiff = trackedFields.some(
        (key) => (updated[key] ?? '') !== (laneOriginalData[key] ?? '')
      );

      setLaneHasChanges(hasDiff);
      setLaneError(null);
    };

    // Populates every field for one via-point slot at once from a selected facility Location,
    // instead of firing handleLaneFieldChange per field (which would recompute "has changes"
    // and re-render once per field for no benefit here).
    const handleSelectViaPointFacility = (slotPrefix, location) => {
      const updated = {
        ...laneEditData,
        [slotPrefix]: location.locationName ?? '',
        [`${slotPrefix}Address1`]: location.addressLine1 ?? '',
        [`${slotPrefix}Address2`]: location.addressLine2 ?? '',
        [`${slotPrefix}City`]: location.city ?? '',
        [`${slotPrefix}State`]: location.state ?? '',
        [`${slotPrefix}Country`]: location.country ?? '',
        [`${slotPrefix}Zip`]: location.zipCode ?? '',
      };
      setLaneEditData(updated);
      setLaneHasChanges(true);
      setLaneError(null);
    };

    const handleLaneSave = async () => {
      setLaneSaving(true);
      setLaneError(null);

      try {
        // Send the full laneEditData – it’s based on the original lane plus edits,
        // so backend gets all required fields (laneCode, contractCode, etc).
        const payload = {
          ...laneEditData,
        };
        const response = await laneApi.updateLane(officeCode, payload, token);
        const updatedLane = response.data;

        // Update global lanes state so UI reflects saved values everywhere
        setAssociatedData((prev) => ({
          ...prev,
          lanes: {
            ...prev.lanes,
            data: prev.lanes.data.map((l) =>
              l.id === lane.id ? { ...updatedLane } : l
            ),
          },
        }));

        setLaneOriginalData(updatedLane);
        setLaneEditData(updatedLane);
        setLaneHasChanges(false);
      } catch (err) {
        console.error('Failed to update lane:', err);
        setLaneError('Failed to update lane. Please try again.');
      } finally {
        setLaneSaving(false);
      }
    };

    const handleLaneRevert = () => {
      setLaneEditData(laneOriginalData);
      setLaneHasChanges(false);
      setLaneError(null);
    };

    const renderValue = (label, field) => {
      // helper: renders either plain text or input depending on edit mode
      if (!isEditMode) {
        return lane[field] ?? '—';
      }
      return (
        <input
          type="text"
          value={laneEditData[field] ?? ''}
          onChange={(e) => handleLaneFieldChange(field, e.target.value)}
          className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={label}
        />
      );
    };

    // Same as renderValue, but the via-point Name field gets a type-ahead over the office's
    // existing facilities — selecting one auto-fills the whole address, typing free text still
    // works exactly like a plain input.
    const renderViaPointName = (label, slotPrefix) => {
      if (!isEditMode) {
        return lane[slotPrefix] ?? '—';
      }
      return (
        <FacilityTypeahead
          officeCode={officeCode}
          value={laneEditData[slotPrefix] ?? ''}
          onChange={(text) => handleLaneFieldChange(slotPrefix, text)}
          onSelect={(location) => handleSelectViaPointFacility(slotPrefix, location)}
          placeholder={label}
        />
      );
    };

    return (
      <ExpandableHeader
        headerSection="Lane"
        headerLabel={`${lane.laneCode}\u00A0\u00A0${lane.name ? `— \u00A0${lane.name}` : ''}`}
        isExpanded={expanded}
        onToggle={() => toggleHierarchicalExpand(lane.id, 'lane')}
        icon={MapPin}
        headerType="primary"
      >
        <div className="px-4 py-3 mb-2">
          {laneError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {laneError}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <DisplayDetail label="Lane Code" value={lane.laneCode} />
            <DisplayDetail label="Office Code" value={lane.officeCode} />
            <DisplayDetail 
              label="Name" 
              value={renderValue('Name', 'name')}
            />

            <DisplayDetail label="Origin Type" value={lane.originType} />
            <DisplayDetail
              label="Origin Country"
              value={renderValue('Origin Country', 'originCountry')}
            />
            <DisplayDetail
              label="Origin State"
              value={renderValue('Origin State', 'originState')}
            />
            <DisplayDetail
              label="Origin City"
              value={renderValue('Origin City', 'originCity')}
            />
            <DisplayDetail
              label="Origin Zip Code"
              value={renderValue('Origin Zip', 'origin')}
            />

            <DisplayDetail label="Destination Type" value={lane.destinationType} />
            <DisplayDetail
              label="Destination Country"
              value={renderValue('Destination Country', 'destinationCountry')}
            />
            <DisplayDetail
              label="Destination State"
              value={renderValue('Destination State', 'destinationState')}
            />
            <DisplayDetail
              label="Destination City"
              value={renderValue('Destination City', 'destinationCity')}
            />
            <DisplayDetail
              label="Destination Zip Code"
              value={renderValue('Destination Zip', 'destination')}
            />

            <DisplayDetail
              label="Via Point 1"
              value={renderViaPointName('Via Point 1 Name', 'viaPoint')}
            />
            <DisplayDetail label="Via Point 1 Type" value={lane.viaPointType} />
            <DisplayDetail label="Via Point 2" value={lane.viaPoint2} />
            <DisplayDetail label="Via Point 2 Type" value={lane.viaPoint2Type} />
            <DisplayDetail label="Via Point 3" value={lane.viaPoint3} />
            <DisplayDetail label="Via Point 3 Type" value={lane.viaPoint3Type} />
          </div>

          {/* Via points are real physical stops (unlike origin/destination, which are just
              rate-matching keys) — they need a full address, not just city/state/zip. Only
              shown when actually editing; via point 2 only makes sense once via point 1 has a
              name, matching the "1 then 2" fill order used everywhere else in this feature. */}
          {isEditMode && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2">
                Via Point 1 Address
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                <DisplayDetail label="Address 1" value={renderValue('Address 1', 'viaPointAddress1')} />
                <DisplayDetail label="Address 2" value={renderValue('Address 2', 'viaPointAddress2')} />
                <DisplayDetail label="City" value={renderValue('City', 'viaPointCity')} />
                <DisplayDetail label="State" value={renderValue('State', 'viaPointState')} />
                <DisplayDetail label="Country" value={renderValue('Country', 'viaPointCountry')} />
                <DisplayDetail label="Zip" value={renderValue('Zip', 'viaPointZip')} />
              </div>

              {(laneEditData.viaPoint || laneEditData.viaPoint2) && (
                <>
                  <p className="text-xs font-medium text-gray-500 mt-3 mb-2">
                    Via Point 2 Address (optional)
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    <DisplayDetail label="Name" value={renderViaPointName('Via Point 2 Name', 'viaPoint2')} />
                    <DisplayDetail label="Address 1" value={renderValue('Address 1', 'viaPoint2Address1')} />
                    <DisplayDetail label="Address 2" value={renderValue('Address 2', 'viaPoint2Address2')} />
                    <DisplayDetail label="City" value={renderValue('City', 'viaPoint2City')} />
                    <DisplayDetail label="State" value={renderValue('State', 'viaPoint2State')} />
                    <DisplayDetail label="Country" value={renderValue('Country', 'viaPoint2Country')} />
                    <DisplayDetail label="Zip" value={renderValue('Zip', 'viaPoint2Zip')} />
                  </div>
                </>
              )}
            </div>
          )}

          {isEditMode && laneHasChanges && (
            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-300 mt-4">
              <button
                onClick={handleLaneSave}
                disabled={laneSaving}
                className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Save size={14} />
                <span>{laneSaving ? 'Saving...' : 'Save Lane'}</span>
              </button>
              <button
                onClick={handleLaneRevert}
                disabled={laneSaving}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <X size={14} />
                <span>Revert</span>
              </button>
            </div>
          )}
        </div>

        {hasRoutes && lane.children.map((route) => (
          <HierarchicalRouteItem key={route.id} route={route} />
        ))}
      </ExpandableHeader>
    );
  };


  const HierarchicalRouteItem = ({ route }) => {
    const hasRates = route.children && route.children.length > 0;
    const expanded = isHierarchicalExpanded(route.id, 'route');


    const [routeEditData, setRouteEditData] = useState(route);
    const [routeOriginalData, setRouteOriginalData] = useState(route);
    const [routeHasChanges, setRouteHasChanges] = useState(false);
    const [routeSaving, setRouteSaving] = useState(false);
    const [routeError, setRouteError] = useState(null);

    // // Reset local editor state when route changes (e.g. refetch after save)
    useEffect(() => {
      setRouteEditData(route);
      setRouteOriginalData(route);
      setRouteHasChanges(false);
      setRouteError(null);
    }, [route]);

    // // If user exits global edit mode, drop local unsaved changes
    useEffect(() => {
      if (!isEditMode) {
        setRouteEditData(route);
        setRouteOriginalData(route);
        setRouteHasChanges(false);
        setRouteError(null);
      }
    }, [isEditMode, route]);

    const handleRouteFieldChange = (field, value) => {
      const updated = {
        ...routeEditData,
        [field]: value,
      };
      setRouteEditData(updated);

      // Fields we actually care about for "has changes"
      const trackedFields = [
        'percentageAllocation',
        'agentCode',
      ];

      const hasDiff = trackedFields.some(
        (key) => (updated[key] ?? '') !== (routeOriginalData[key] ?? '')
      );

      setRouteHasChanges(hasDiff);
      setRouteError(null);
    };


    const [toggleState, setToggleState] = useState({
      autoAssign: route.autoAssign,
      autoTender: route.autoTender,
      autoAssignOnRecall: route.autoAssignOnRecall,
    });

    const [hasToggleChanges, setHasToggleChanges] = useState(false);
    const [isSavingToggle, setIsSavingToggle] = useState(false);
    const [toggleError, setToggleError] = useState(null);

    useEffect(() => {
      setToggleState({
        autoAssign: route.autoAssign,
        autoTender: route.autoTender,
        autoAssignOnRecall: route.autoAssignOnRecall,
      });
      setHasToggleChanges(false);
      setToggleError(null);
    }, [route.autoAssign, route.autoTender, route.autoAssignOnRecall]);

    const handleToggleChange = (fieldName, newValue) => {
      const newToggleState = {
        ...toggleState,
        [fieldName]: newValue,
      };

      const hasChanges =
        newToggleState.autoAssign !== routeOriginalData.autoAssign ||
        newToggleState.autoTender !== routeOriginalData.autoTender ||
        newToggleState.autoAssignOnRecall !== routeOriginalData.autoAssignOnRecall;

      setToggleState(newToggleState);
      setHasToggleChanges(hasChanges);
      setToggleError(null);
    };

    const handleSaveToggleChanges = async () => {
      setIsSavingToggle(true);
      setRouteSaving(true);
      setToggleError(null);
      setRouteError(null);

      try {
        const payload = {
          ...routeEditData,
          autoAssign: toggleState.autoAssign,
          autoTender: toggleState.autoTender,
          autoAssignOnRecall: toggleState.autoAssignOnRecall,
        };

        const response = await routeApi.updateRoute(officeCode, route.id, payload);
        const updatedFromBackend = response.data;

        // Update global routes state
        setAssociatedData((prev) => ({
          ...prev,
          routes: {
            ...prev.routes,
            data: prev.routes.data.map((r) =>
              r.id === route.id ? { ...updatedFromBackend } : r
            ),
          },
        }));

        // Update local "original" + "edit" snapshots so diffs work properly
        setRouteOriginalData(updatedFromBackend);
        setRouteEditData(updatedFromBackend);

        // Clear change flags
        setHasToggleChanges(false);
        setRouteHasChanges(false);

        setIsEditMode(false);

      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to save changes. Please try again.';
        console.error("Failed to save route changes:", error);
        setToggleError(msg);
      } finally {
        setIsSavingToggle(false);
        setRouteSaving(false);
      }
    };


    const handleRevertToggleChanges = () => {
      // revert route editable fields
      setRouteEditData(routeOriginalData);

      // revert toggles
      setToggleState({
        autoAssign: routeOriginalData.autoAssign,
        autoTender: routeOriginalData.autoTender,
        autoAssignOnRecall: routeOriginalData.autoAssignOnRecall,
      });

      // clear flags/errors
      setHasToggleChanges(false);
      setRouteHasChanges(false);
      setToggleError(null);
      setRouteError(null);

      // match lane behavior
      setIsEditMode(false);

    };


    const renderValue = (label, field) => {
      // helper: renders either plain text or input depending on edit mode
      if (!isEditMode) {
        return route[field] ?? '—';
      }
      return (
        <input
          type="text"
          value={routeEditData[field] ?? ''}
          onChange={(e) => handleRouteFieldChange(field, e.target.value)}
          className="mt-1 block w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder={label}
        />
      );
    };

    return (
      <div className="ml-4 mr-2 mb-2 last:mb-4">
        <ExpandableHeader
          headerSection="Route"
          headerLabel={route.routeCode}
          isExpanded={expanded}
          onToggle={() => toggleHierarchicalExpand(route.id, 'route')}
          // count={hasRates ? route.children.length : 0}
          icon={Route}
          headerType="secondary"
        >
          <div className="bg-[#FAFBFC] pb-2">
            <div className="px-4 py-3 border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <DisplayDetail label="Route Code" value={route.routeCode} />
                <DisplayDetail label="Carrier" value={route.carrier} />
                <DisplayDetail label="Carrier Code" value={route.carrierCode} />
                <DisplayDetail label="Agent Name" value={route.agentName} />
                <DisplayDetail label="Agent Code" value={renderValue('Agent Code', 'agentCode')} />
                <DisplayDetail label="Mode" value={route.mode} />
                <DisplayDetail
                  label="Capacity"
                  value={
                    route.capacity != null && route.capacity !== ''
                      ? `${route.capacity} ${route.capacityUnit}`
                      : '-'
                  }
                />
                <DisplayDetail
                  label="Commitment"
                  value={
                    route.commitment != null && route.commitment !== ''
                      ? `${route.commitment} ${route.commitmentUnit}`
                      : '-'
                  }
                />
                <DisplayDetail
                  label="Customer Nominated Carrier"
                  value={route.customerNominatedCarrier ? 'Yes' : 'No'}
                />
                <DisplayDetail
                  label="Percentage Allocation"
                  disabled={routeSaving}
                  value={renderValue('Percentage Allocation', 'percentageAllocation')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-gray-100">
                <ToggleSwitch
                  label="Auto Assign"
                  name="autoAssign"
                  checked={toggleState.autoAssign}
                  onChange={(e) =>
                    isEditMode && handleToggleChange('autoAssign', e.target.checked)
                  }
                  disabled={!isEditMode}
                />
                <ToggleSwitch
                  label="Auto Tender"
                  name="autoTender"
                  checked={toggleState.autoTender}
                  onChange={(e) =>
                    isEditMode && handleToggleChange('autoTender', e.target.checked)
                  }
                  disabled={!isEditMode}
                />
                <ToggleSwitch
                  label="Auto Assign on Recall"
                  name="autoAssignOnRecall"
                  checked={toggleState.autoAssignOnRecall}
                  onChange={(e) =>
                    isEditMode && handleToggleChange('autoAssignOnRecall', e.target.checked)
                  }
                  disabled={!isEditMode}
                />
              </div>

              {toggleError && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  {toggleError}
                </div>
              )}

              {isEditMode && (hasToggleChanges || routeHasChanges) && (
                <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-300 mt-4">
                  <button
                    onClick={handleSaveToggleChanges}
                    disabled={isSavingToggle}
                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Save size={14} />
                    <span>{isSavingToggle ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                  <button
                    onClick={handleRevertToggleChanges}
                    disabled={isSavingToggle}
                    className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <X size={14} />
                    <span>Revert</span>
                  </button>
                </div>
              )}
            </div>

            {hasRates && (
              <div className="ml-4 mr-2 mb-4">
                {route.children.map((rate) => {
                  const globalRateIndex = associatedData.rates.data.findIndex((r) => r.id === rate.id);
                  return (
                    <HierarchicalRateItem key={rate.id} rate={rate} rateIndex={globalRateIndex} />
                  );
                })}
              </div>
            )}
          </div>
        </ExpandableHeader>
      </div>
    );
  };

    const HierarchicalRateItem = ({ rate, rateIndex }) => {
    const accessorial = accessorialMap[rate.rateType] || {};
    const expanded = isHierarchicalExpanded(rate.id, 'rate');

    const handleCopyRate = () => {
      const allRateCodes = associatedData.rates.data.map((r) => r.rateCode);
      const baseMatch = rate.rateCode.match(/^(.*?)(?:-\d+)?$/);
      const baseCode = baseMatch?.[1] || rate.rateCode;

      const matchingSuffixes = allRateCodes
        .filter((code) => code.startsWith(baseCode))
        .map((code) => {
          const match = code.match(new RegExp(`^${baseCode}-(\\d+)$`));
          return match ? parseInt(match[1], 10) : 0;
        });

      const nextSuffix =
        matchingSuffixes.length === 0 ? 1 : Math.max(...matchingSuffixes) + 1;

      const newRateCode = `${baseCode}-${nextSuffix}`;

      const copiedRate = {
        ...rate,
        id: `${rate.id}_copy_${Date.now()}`,
        rateCode: newRateCode,
        isTemp: true,
      };

      handleRateAdd(rateIndex, copiedRate);
    };

    return (
      <div className="bg-white dark:bg-acs-dark-surface mb-2 relative">
        <ExpandableHeader
          headerSection="Rate"
          headerLabel={rate.rateCode}
          isExpanded={expanded}
          onToggle={() => toggleHierarchicalExpand(rate.id, 'rate')}
          icon={DollarSign}
          headerType="tertiary"
        >

          {/* Rates are always read-only regardless of global edit mode
           isGlobalEditMode={false} ensures no fields are editable and
           Save/Revert buttons never appear in RateEditor */}

          <div className="px-4 py-3 mb-4">
            <RateEditor
              rate={rate}
              rateIndex={rateIndex}
              officeCode={contractData.officeCode}
              accessorialMap={accessorialMap}
              isGlobalEditMode={isEditMode}   //Rates are always read-only
              onRateUpdate={handleRateUpdate}
              onRateAdd={handleRateAdd}
              onRateListUpdate={handleRateRemove}
            />

            {accessorial.conditionNotes && accessorial.conditionNotes !== '-' && (
              <div className="mt-3 pt-3 ml-3">
                <div className="flex items-start">
                  <div className="flex-1 border-l-2 border-gray-300 pl-3">
                    <div className="flex items-center space-x-2">
                      <FileText size={13} className="text-gray-500" />
                      <p className="text-sm font-medium text-gray-700 mb-0">Condition Notes</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 ml-6 mb-2">
                      {accessorial.conditionNotes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ExpandableHeader>

        {/* TODO: Copy rate button is disabled — rates are read-only in this view
    Keeping code for future reference if copy functionality is needed (change false to isEditMode)*/}
        {false && (   
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopyRate();
            }}
            className="absolute top-5 right-4 transform -translate-y-[40%] flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors z-10"
            title="Copy this rate"
          >
            <Plus size={14} />
          </button>
        )}
      </div>
    );
  };

  // Summary stats
  const laneCount = hierarchicalData.length;
  const routeCount = hierarchicalData.reduce((sum, lane) => sum + (lane.children?.length || 0), 0);
  const rateCount = hierarchicalData.reduce(
    (sum, lane) =>
      sum +
      (lane.children?.reduce(
        (routeSum, route) => routeSum + (route.children?.length || 0),
        0
      ) || 0),
    0
  );

  return (
    <div className="w-full h-full min-h-0 flex flex-col transition-opacity duration-500 bg-gray-50">
      <div
        ref={headerRef}
        data-app-sticky-header
        className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md w-full"
      >
        <StickyHeader
          infoData={headerInfo}
          buttons={headerButtons}
          mode={null}
          leftLabel="Contract Details"
          handleExportData={handleExportData}
          // onInfoChange={handleHeaderChange}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
  
  {/* Active Rate Toggle — filters rates by endDate >= today when enabled */}
  <div className="flex items-center justify-end gap-2 px-6 py-2 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border">
    <span className="text-sm text-gray-600 font-medium">Active</span>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={activeOnly}
        onChange={(e) => setActiveOnly(e.target.checked)}
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
      <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-full"></div>
    </label>
  </div>
        <div className="p-6 space-y-6">

          {/* Loading Indicator */}
          {associatedData.loading && <div className="text-center py-4">Loading associated data...</div>}

          {/* Hierarchical View Section */}
          {!associatedData.loading && hierarchicalData.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Contract Summary</h2>
                <div className="flex items-center gap-4">
                  {/* Summary Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{laneCount} {laneCount === 1 ? 'Lane' : 'Lanes'}</span>
                    <span>{routeCount} {routeCount === 1 ? 'Route' : 'Routes'}</span>
                    {/* Show "X Active Rates" when toggle is ON, "X Rates" when toggle is OFF */}
                    <span>{rateCount} {activeOnly ? 'Active ' : ''}{rateCount === 1 ? 'Rate' : 'Rates'}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={expandAllHierarchical}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
                    >
                      Expand All
                    </button>
                    <button
                      onClick={collapseAllHierarchical}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
                    >
                      Collapse All
                    </button>
                  </div>
                </div>
              </div>

              {<div className="space-y-4">
                  {hierarchicalData.map(lane => (
                    <HierarchicalLaneItem key={lane.id} lane={lane} />
                  ))}
                </div>
              }
            </div>
          )}

          {!associatedData.loading && hierarchicalData.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No contract data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
